<template>
    <div class="main-container px-2 pt-4 md:px-8 ">
        <!-- Mobile View -->
        <div v-if="isMobile">
            <Accordion value="0" class="card-bg shadow-md rounded-xl">
                <AccordionPanel>
                    <AccordionHeader>
                        <span class="font-semibold text-lg pl-5">Date Filters</span>
                    </AccordionHeader>
                    <AccordionContent>
                        <DateFilters 
                            v-model:dates="dates"
                            v-model:selectedMonthIndex="selectedMonthIndex"
                            v-model:selectedDatePreset="selectedDatePreset"
                            :months="months"
                            :datePresets="datePresets"
                            :isMobile="isMobile"
                            @monthSelect="handleMonthSelect"
                            @datePresetChange="handleDatePresetChange"
                        />
                    </AccordionContent>
                </AccordionPanel>
            </Accordion>

            <!-- Month Buttons for Mobile -->
            <div class="flex justify-center items-center gap-4 mt-3">
                <Button icon="pi pi-chevron-left" @click="navigateMonth(-1)" :disabled="!canNavigateBack"
                    severity="secondary" outlined class="p-button-text h-7" />
                <Button :severity="currentMonthLabel ? 'primary' : 'secondary'" class="min-w-[100px] font-semibold h-7">
                    {{ currentMonthLabel || 'Custom Range' }}
                </Button>
                <Button icon="pi pi-chevron-right" @click="navigateMonth(1)" :disabled="!canNavigateForward"
                    severity="secondary" outlined class="p-button-text h-7" />
            </div>
        </div>

        <!-- Desktop View -->
        <div v-else class="card-bg shadow-md rounded-xl p-4">
            <DateFilters 
                v-model:dates="dates"
                v-model:selectedMonthIndex="selectedMonthIndex"
                v-model:selectedDatePreset="selectedDatePreset"
                :months="months"
                :datePresets="datePresets"
                :isMobile="isMobile"
                @monthSelect="handleMonthSelect"
                @datePresetChange="handleDatePresetChange"
            />
        </div>

        <Skeleton v-if="loading" class="mt-10" width="100%" height="60vh"></Skeleton>

        <DataTable v-else :value="siteEarnings" :virtualScrollerOptions="{ itemSize: 60 }" scrollable
            :scrollHeight="'72svh'" :loading="loading" class="rounded-xl shadow-sm md:col-span-1 mt-5" fluid
            rowGroupMode="rowspan" groupRowsBy="siteName" sortMode="single" :sortField="'siteName'"
            :sortOrder="1" dataKey="advertiserId">
            <Column field="siteName" :header="siteColumnHeader" :headerStyle="headerStyles" frozen
            alignFrozen="left" footer="Totals">
            </Column>
            <Column field="advertiserName" header="Advertiser" :headerStyle="headerStyles" >
            </Column>
            <Column field="publisherAlias" :headerStyle="headerStyles">
                <template #header>
                    <div class="flex items-center gap-2">
                        <span>Code</span>
                        <i 
                            class="pi pi-info-circle text-sm text-gray-500 cursor-pointer"
                            v-tooltip.bottom="'Click here to set the internal code'"
                            @click="$router.push('/sites?showing=links')"
                        />
                    </div>
                </template>
            </Column>
            <Column field="clicks" header="C's"  :footer="totalClicks"
                :headerStyle="headerStyles" />
            <Column field="uniqueClicks" header="UC's"  :footer="totalUniqueClicks"  :headerStyle="headerStyles" />
            <Column field="signups" header="SU's"  :footer="totalSignups" :headerStyle="headerStyles" />
            <Column field="FTD" header="FTDs" sortable  :footer="totalFTD" :headerStyle="headerStyles" />
            <Column field="CPACount" header="CPAs" sortable  :footer="totalCPAs" :headerStyle="headerStyles" />
            <Column field="deposits" header="Deposits"  :footer="totalDeposits" :headerStyle="headerStyles" />
            <Column field="depositValue" header="D Value" :footer="totalDepositValue"
                :headerStyle="headerStyles">
                <template #body="slotProps">
                    <div>
                        <!-- Call the formatCurrency function, now it always formats in Euro -->
                        <p>{{ formatCurrency(slotProps.data.depositValue) }}</p> <!-- Output: €6,031.56 -->
                    </div>
                </template>
            </Column>
            <!-- <Column 
                field="customerExpectedRevenueAfterCommission" 
                sortable 
                :footer="totalExpectedIncome"
                :headerStyle="headerStyles"
            >
                <template #header>
                    <div class="flex items-center gap-2 p-datatable-column-title">
                        <span>Expected Income</span>
                        <i 
                            class="pi pi-info-circle text-sm text-gray-500 cursor-help"
                            v-tooltip.bottom="TOOLTIPS.QUALIFIED_USERS_ESTIMATE"
                        ></i>
                    </div>
                </template>
                <template #body="slotProps">
                    <div :class="{ 'income-cell': slotProps.data.customerExpectedRevenueAfterCommission > 0 }">
                        <p>{{ !slotProps.data.customerExpectedRevenueAfterCommission ? '-' : formatCurrency(slotProps.data.customerExpectedRevenueAfterCommission) }}</p>
                    </div>
                </template>
            </Column> -->
            <Column  header="Income" sortable :footer="totalMyIncome"
                :headerStyle="headerStyles">
                <template #body="{ data }">
                    <div :class="{ 'income-cell': getTotalEarnings(data.customerCPRevenue, data.customerRevshareRevenue) > 0 }">
                        <!-- Call the formatCurrency function, now it always formats in Euro -->
                        <p>{{ formatCurrency(getTotalEarnings(data.customerCPRevenue, data.customerRevshareRevenue)) }}</p>
                        <!-- Output: €6,031.56 -->
                    </div>
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script setup>
import { headerStyles, getRowStyle } from '@/global/headerStyles';
import { formatCurrency } from '@/utils/currencies';
import { ref, onMounted, watch, computed, onUnmounted } from "vue";
import { presets, getMonthsForYear, getCurrentMonthIndex, getLastFourMonths, findCurrentMonthIndex } from '@/utils/dateHelper';
import EarningsService from "@/services/EarningsService";
import { useStore } from 'vuex';
import { getTotalEarnings } from '@/utils/earnings';
import DateFilters from './DateFilters.vue';

const store = useStore();
const user = computed(() => store.state.user);
const siteColumnHeader = computed(() =>
    store.getters.isSeo ? 'Site' : 'Toplist'
)
// To render months
const months = ref(getLastFourMonths());
const selectedMonthIndex = ref(findCurrentMonthIndex(months.value));

const selectedMonth = (index, from, to) => {
    selectedDatePreset.value = null;
    selectedMonthIndex.value = index;
    dates.value = [from, to];
};

// Dates
const datePresets = ref(presets);
const selectedDatePreset = ref(null); // Remove default preset
const dates = ref([
    months.value[selectedMonthIndex.value].start,
    months.value[selectedMonthIndex.value].end
]);

const handleDatePresetChange = () => {
    selectedMonthIndex.value = null; // Reset the selected month index
    console.log('Selected date preset changed to:', selectedDatePreset.value);
    dates.value = [selectedDatePreset.value.from, selectedDatePreset.value.to, 'preset'];
};

const loading = ref(true);

// Earnings
const siteEarnings = ref([]);
const totalClicks = computed(() => siteEarnings.value.reduce((sum, item) => sum + (item.clicks || 0), 0));
const totalUniqueClicks = computed(() => siteEarnings.value.reduce((sum, item) => sum + (item.uniqueClicks || 0), 0));
const totalSignups = computed(() => siteEarnings.value.reduce((sum, item) => sum + (item.signups || 0), 0));
const totalFTD = computed(() => siteEarnings.value.reduce((sum, item) => sum + (item.FTD || 0), 0));
const totalCPAs = computed(() => siteEarnings.value.reduce((sum, item) => sum + (item.CPACount || 0), 0));
const totalDeposits = computed(() => siteEarnings.value.reduce((sum, item) => sum + (item.deposits || 0), 0));
const totalDepositValue = computed(() => formatCurrency(siteEarnings.value.reduce((sum, item) => sum + (item.depositValue || 0), 0)));
const totalMyIncome = computed(() => formatCurrency(siteEarnings.value.reduce((sum, item) => sum + (getTotalEarnings(item.customerCPRevenue, item.customerRevshareRevenue) || 0), 0)));
const totalExpectedIncome = computed(() => formatCurrency(siteEarnings.value.reduce((sum, item) => sum + (item.customerExpectedRevenueAfterCommission || 0), 0)));

async function GetSiteEarnings(from, to) {
    loading.value = true;
    resetEarnings();
    await EarningsService.getBySite(from ?? selectedDatePreset.value.from, to ?? selectedDatePreset.value.to)
        .then((response) => {
            siteEarnings.value = response.data;
        }).finally(() => loading.value = false);
}

function resetEarnings() {
    siteEarnings.value = [];
}

// Update your date watcher
watch(dates, async (newDate) => {
    if (newDate[1] !== null) {
        if (newDate[2] !== 'preset')
            selectedDatePreset.value = null;

        if (newDate[1].getHours() === 0)
            selectedMonthIndex.value = null;

        await GetSiteEarnings(newDate[0], newDate[1]);
    }
});

onMounted(async () => {
    // Load current month's earnings on mount
    await GetSiteEarnings(
        months.value[selectedMonthIndex.value].start,
        months.value[selectedMonthIndex.value].end
    );
});

// Add mobile detection
const isMobile = ref(window.innerWidth <= 768);

const handleResize = () => {
    isMobile.value = window.innerWidth <= 768;
};

const handleMonthSelect = ({ index, start, end }) => {
    selectedDatePreset.value = null;
    selectedMonthIndex.value = index;
    dates.value = [start, end];
};

const canNavigateBack = computed(() => selectedMonthIndex.value > 0);
const canNavigateForward = computed(() => selectedMonthIndex.value < months.value.length - 1);
const currentMonthLabel = computed(() => months.value[selectedMonthIndex.value]?.label || '');

const navigateMonth = (direction) => {
    const newIndex = selectedMonthIndex.value + direction;
    if (newIndex >= 0 && newIndex < months.value.length) {
        selectedMonth(newIndex, months.value[newIndex].start, months.value[newIndex].end);
    }
};

onMounted(() => {
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
/* Ensure DatePicker input is full width on mobile */
:deep(.p-calendar) {
    width: 100%;
}

:deep(.p-calendar .p-inputtext) {
    width: 100%;
}

:deep(.p-dropdown) {
    width: 100%;
}

@media (min-width: 640px) {
    :deep(.p-calendar),
    :deep(.p-calendar .p-inputtext) {
        width: auto;
    }
    
    :deep(.p-dropdown) {
        min-width: 200px;
    }
}

.p-accordionheader {
    padding: 5px !important;
}

.p-accordionpanel {
    border-width: 0 0 0 0;
}
</style>